import { Box, CardContent, Checkbox, Divider, FormControlLabel, FormGroup, Grid, Stack, Typography } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcButton, MprcCard, MprcChipChoice, MprcFormSubHeader, MprcInfiniteLoading, MprcPrompt, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonSave } from "components/buttons";
import { MprcBarTitle } from "components/MprcBarTitle";
import { MprcContainer } from "components/MprcContainer";
import { AuditLogEntityType, KnowledgeQuickFilterScope, SelectionQuickFilterItemResponse } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AuditLogHistoryFooter } from "views/common/AuditLog/AuditLogHistoryFooter";
import { EditSettingsHeader } from "views/foundation/settings/components/EditSettingsHeader";
import { ListQuickFilters } from "./ListQuickFilters";

interface ScopeOption {
  label: string;
  type: KnowledgeQuickFilterScope;
}

interface IProps {
  scope?: KnowledgeQuickFilterScope;
  scopeRef?: string;
}

////////////////////////////////////////////////////////////////////////////
/**
 * Seleção dos Filtros Rápidos
 */
export const SelectionQuickFilter: React.FC<IProps> = ({ scopeRef, ...props }) => {

  const api = useCoreApi();
  const navigate = useNavigate();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();

  const [availableData, setAvailableData] = useState<SelectionQuickFilterItemResponse[]>([]);
  const [selectedData, setSelectedData] = useState<SelectionQuickFilterItemResponse[]>([]);
  const [checkAllAvailable, setCheckAllAvailable] = useState<boolean>(false);
  const [checkAllSelected, setCheckAllSelected] = useState<boolean>(false);
  const loadDto = useRef<SelectionQuickFilterItemResponse[]>([]);
  const [listCustom, setListCustom] = useState<boolean>(false);
  const [scope, setScope] = useState<KnowledgeQuickFilterScope>(props.scope ?? KnowledgeQuickFilterScope.ForYou);
  const [pendingScope, setPendingScope] = useState<KnowledgeQuickFilterScope | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [canSave, setCanSave] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);
  const [recordChanged, setRecordChanged] = useState<boolean>(false);

  const isCustomScope = scopeRef != null;
  const scopeName = useRef<string | null>(null);

  ////////////////////////////////////////////////////////////////////////////

  const scopeList: ScopeOption[] = useMemo(() => [
    { type: KnowledgeQuickFilterScope.ForYou, label: t("QuickFilter.Selection.Scope.ForYou") },
    { type: KnowledgeQuickFilterScope.Channel, label: t("QuickFilter.Selection.Scope.Channel") },
    { type: KnowledgeQuickFilterScope.Topic, label: t("QuickFilter.Selection.Scope.Topic") },
    { type: KnowledgeQuickFilterScope.Author, label: t("QuickFilter.Selection.Scope.Author") }
  ], [t]);

  ////////////////////////////////////////////////////////////////////////////

  ///
  const getHeaderItems = () => {
    const formHeaderOptions: React.ReactNode[] = [];

    if (listCustom) {
      formHeaderOptions.push(
        <Stack direction="column" alignItems="end">
          <Typography variant="h4">{t("QuickFilter.List.ViewTitle")}</Typography>
          <Typography variant="caption" textTransform="uppercase">{scopeName.current ?? scopeList.find(p => p.type == scope)?.label}</Typography>
        </Stack >
      );
    }
    else {
      formHeaderOptions.push(<MprcBarTitle key="barTitle">{scopeName.current ?? t("QuickFilter.Selection.ViewTitle")}</MprcBarTitle>);
      formHeaderOptions.push(<ButtonSave key="btnSave" rounded size="large" variant="text" onClick={() => { setSaving(true); }} disabled={!canSave} />);
    }
    return formHeaderOptions;
  };

  ///
  const refreshData = (dto: SelectionQuickFilterItemResponse[]) => {
    const selectedItems = dto.length == 0 ? [] : dto.filter(x => x.isSelected);
    const availableItems = dto.length == 0 ? [] : dto.filter(x => !x.isSelected).sort((a, b) => a.name.localeCompare(b.name));
    loadDto.current = selectedItems.length == 0 ? [] : dto.filter(x => x.isSelected);

    setSelectedData(selectedItems);
    setAvailableData(availableItems);
  };

  ///
  const handleAvailableCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItems = availableData?.map(obj => {
      return { ...obj, isChecked: event.target.checked };
    });

    setAvailableData(newItems);
    setCheckAllAvailable(event.target.checked);
  };

  ///
  const handleAvailableCheckItem = (event: React.ChangeEvent<HTMLInputElement>, item: SelectionQuickFilterItemResponse) => {
    const newItems = availableData?.map(x => {
      if (x.quickFilterId === item.quickFilterId) {
        return { ...x, isChecked: event.target.checked };
      }
      return x;
    });

    setAvailableData(newItems);
  };

  ///
  const handleSelectedCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newItems = selectedData?.map(obj => {
      return { ...obj, isChecked: event.target.checked };
    });

    setSelectedData(newItems);
    setCheckAllSelected(event.target.checked);
  };

  ///
  const handleSelectedCheckItem = (event: React.ChangeEvent<HTMLInputElement>, item: SelectionQuickFilterItemResponse) => {
    const newItems = selectedData?.map(x => {
      if (x.quickFilterId === item.quickFilterId) {
        return { ...x, isChecked: event.target.checked };
      }
      return x;
    });

    setSelectedData(newItems);
  };

  ///
  const handleMoveAvailableToSelected = () => {
    const selectedItems = availableData.filter(x => x.isChecked)
      .map(x => ({ ...x, isChecked: false }));

    setSelectedData([...selectedData, ...selectedItems]);
    setAvailableData(availableData.filter(x => !x.isChecked));
  };

  ///
  const handleMoveSelectedToAvailable = () => {
    const selectedItems = selectedData.filter(x => x.isChecked)
      .map(x => ({ ...x, isChecked: false }));

    setAvailableData([...availableData, ...selectedItems]);
    setSelectedData(selectedData.filter(x => !x.isChecked));
  };

  ///
  const handleChangeScope = (scope: KnowledgeQuickFilterScope, force: boolean) => {
    if (recordChanged && !force) {
      setPendingScope(scope);
    }
    else {
      setPendingScope(null);
      setScope(scope);
      setLoading(true);
    }
  };

  ////////////////////////////////////////////////////////////////////////////
  /// EFFECT's
  ////////////////////////////////////////////////////////////////////////////

  // Ao carregar um DTO:
  useEffect(() => {
    async function getData() {
      try {
        let result = await api.v1.knowledgeQuickFilter.getSelectionQuickFilter({ scope, scopeRef: scopeRef ?? null });
        let data = result.data;
        if (scopeName.current == null) {
          scopeName.current = data.scopeName;
        }
        refreshData(data.items);
      } catch (e: any) {
        setViewError(e);
      }
      finally {
        setLoading(false);
      }
    }

    if (loading) getData();

  }, [loading]);

  //// Ao salvar um DTO:
  useEffect(() => {
    async function postData() {
      setSaving(true);
      try {
        var success = await api.v1.knowledgeQuickFilter.selectQuickFilters({
          scope,
          scopeRef: scopeRef ?? null,
          quickFilterIds: selectedData.map(x => x.quickFilterId)
        });

        if (success) {
          enqueueSnackbar(t("QuickFilter.Selection.SelectionSuccessMessage"), { variant: "success" });
          refreshData([...availableData.map(p => { p.isSelected = false; return p; }), ...selectedData.map(p => { p.isSelected = true; return p; })]);
        }
        else enqueueSnackbar(t("QuickFilter.Selection.SelectionErrorMessage"), { variant: "error" });


      } catch (e) {
        setViewError(e);
      }
      finally {
        setSaving(false);
      }
    }

    if (saving) postData();

  }, [saving]);

  /// Ao marcar itens disponíveis
  useEffect(() => {
    const isAllChecked = !availableData.some(x => !x.isChecked);
    setCheckAllAvailable(isAllChecked);
  }, [availableData]);

  /// Ao marcar items selecionados
  useEffect(() => {
    const isAllChecked = !selectedData.some(x => !x.isChecked);
    setCheckAllSelected(isAllChecked);

    const changed = !LibUtils.deepCompare(selectedData.map(x => x.quickFilterId).sort(), loadDto.current.map(x => x.quickFilterId).sort());
    setRecordChanged(changed);
    setCanSave(changed);
  }, [selectedData]);

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  const RenderAvailableList = (
    <MprcCard sx={{ height: "100%" }}>
      <CardContent>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={checkAllAvailable}
                onChange={handleAvailableCheckAll}
              />
            }
            label={t("QuickFilter.Selection.AvailableText")}
            disabled={availableData.length == 0}
          />
        </FormGroup>
        <Divider component="hr" />
        <FormGroup>
          {availableData?.map(item =>
            <FormControlLabel key={item.quickFilterId}
              control={
                <Checkbox
                  checked={item.isChecked}
                  onChange={(event) => handleAvailableCheckItem(event, item)} />
              }
              label={item.name}
            />
          )}
        </FormGroup>
      </CardContent>
    </MprcCard>
  );

  ///
  const RenderSelectedList = (
    <MprcCard sx={{ height: "100%" }}>
      <CardContent>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={checkAllSelected}
                onChange={handleSelectedCheckAll}
              />
            }
            label={t("QuickFilter.Selection.SelectedText")}
            disabled={selectedData.length == 0}
          />
        </FormGroup>
        <Divider component="hr" />
        <FormGroup>
          <FormGroup>
            {selectedData?.map(item =>
              <FormControlLabel key={item.quickFilterId}
                control={
                  <Checkbox
                    checked={item.isChecked}
                    onChange={(event) => handleSelectedCheckItem(event, item)}
                  />
                }
                label={item.name}
              />
            )}
          </FormGroup>
        </FormGroup>
      </CardContent>
    </MprcCard>
  );

  ///
  const RenderActions = (
    <Grid container direction="column" alignItems="center" spacing={2}>
      <Grid>
        <MprcButton
          text={">"}
          disabled={!availableData.some(x => x.isChecked)}
          title={t("CustomField.Edit.TestButtonTooltip")}
          variant="contained"
          onClick={() => handleMoveAvailableToSelected()}
        />
      </Grid>
      <Grid>
        <MprcButton
          text={"<"}
          disabled={!selectedData.some(x => x.isChecked)}
          title={t("CustomField.Edit.TestButtonTooltip")}
          variant="contained"
          onClick={() => handleMoveSelectedToAvailable()}
        />
      </Grid>
    </Grid>
  );

  ///
  const RenderSelection = () => {
    return <>
      <MprcContainer>
        {!isCustomScope &&
          <Stack direction="row" justifyContent="center">
            {scopeList.map(item =>
              <MprcChipChoice
                key={item.type}
                label={item.label}
                selected={scope == item.type}
                uppercase
                onClick={() => handleChangeScope(item.type, false)}
              />
            )}
          </Stack>
        }
      </MprcContainer>
      {loading
        ? <MprcInfiniteLoading visible />
        : (
          <MprcContainer>
            <MprcCard sx={{ mb: 2 }}>
              <CardContent>
                <MprcContainer margin="large">
                  <MprcFormSubHeader
                    title={t("QuickFilter.Selection.Title")}
                    subtitle={
                      scope == KnowledgeQuickFilterScope.Channel && isCustomScope
                        ? t("QuickFilter.Selection.TitleChannelText")
                        : scope == KnowledgeQuickFilterScope.Topic && isCustomScope
                          ? t("QuickFilter.Selection.TitleTopicText")
                          : t("QuickFilter.Selection.TitleText")
                    }
                    margin="dense"
                  />
                  <Box display="flex">
                    <Box display="flex" flexDirection="column" flexBasis="100%" flex={1}>
                      {RenderAvailableList}
                    </Box>

                    <Box display="flex" flexDirection="column" justifyContent="center" ml={2} mr={2}>
                      {RenderActions}
                    </Box>

                    <Box display="flex" flexDirection="column" justifyContent="stretch" flexBasis="100%" flex={1}>
                      {RenderSelectedList}
                    </Box>
                  </Box>
                </MprcContainer>

                <MprcContainer margin="large">
                  <MprcFormSubHeader
                    title={t("QuickFilter.Selection.CustomTitle")}
                    subtitle={
                      scope == KnowledgeQuickFilterScope.Channel && isCustomScope
                        ? t("QuickFilter.Selection.CustomTitleChannelText")
                        : scope == KnowledgeQuickFilterScope.Topic && isCustomScope
                          ? t("QuickFilter.Selection.CustomTitleTopicText")
                          : t("QuickFilter.Selection.CustomTitleText")}
                    margin="dense"
                  />
                  <Grid container>
                    <Grid>
                      <MprcButton
                        variant="contained"
                        text={
                          scope == KnowledgeQuickFilterScope.Channel && isCustomScope
                            ? t("QuickFilter.Selection.ManageCustomChannelButtonText")
                            : scope == KnowledgeQuickFilterScope.Topic && isCustomScope
                              ? t("QuickFilter.Selection.ManageCustomTopicButtonText")
                              : t("QuickFilter.Selection.ManageCustomButtonText")
                        }
                        title={t("QuickFilter.Selection.ManageCustomButtonTooltip")}
                        disabled={canSave}
                        onClick={() => setListCustom(true)}
                      />
                    </Grid>
                  </Grid>
                </MprcContainer>

              </CardContent>
            </MprcCard>

            {!isCustomScope &&
              <MprcContainer>
                <AuditLogHistoryFooter entityType={AuditLogEntityType.KnowledgeQuickFilter} entityKey={[KnowledgeQuickFilterScope[scope] as string]} />
              </MprcContainer>
            }
          </MprcContainer>
        )
      }
    </>;
  };

  ////////////////////////////////////////////////////////////////////////////

  return (
    <Grid container direction="column" height={"100%"}>
      <Grid>
        <EditSettingsHeader
          recordChanged={recordChanged}
          actions={getHeaderItems()}
          onBack={
            () => {
              if (listCustom) {
                setListCustom(false);
                setLoading(true);
              }
              else navigate(-1);
            }
          }
        />
        <MprcPrompt open={pendingScope != null} onCancel={() => setPendingScope(null)} onConfirm={() => handleChangeScope(pendingScope!, true)} />
      </Grid>
      <Grid size="grow" overflow="hidden" sx={{ marginTop: 2 }}>
        <Grid container direction="column" height="100%">
          <Grid size="grow" overflow="auto" className={AppGlobalClassKeys.thinScrollbar}>

            <MprcRequestIndicator visible={saving} type={"post"} />

            {!loading && viewError != null && selectedData.length == 0
              ? <MprcRequestErrorReport error={viewError} />
              : (
                <>
                  <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(undefined)} />
                  {
                    listCustom
                      ? <ListQuickFilters scope={scope} scopeRef={scopeRef ?? null} />
                      : RenderSelection()
                  }
                </>
              )
            }
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
